import React, { useRef, useState, useEffect } from 'react';
import { useMetricType } from 'hooks/useMetricType';
import { IconButton, Tooltip } from '@mui/material';
import { validateEditPercent, validateEditSize } from 'util/validatePsdInput';
import { useOutsideClickListener } from './useOutsideClickListener';
import {
  PsdActionCell,
  PsdCheckboxCell,
  PsdGridRow,
  PsdPercentCell,
  PsdSizeCell,
} from 'containers/PropertyPanel/Cards/Feed/PsdGrid';
import { PsdNumberField } from 'containers/PropertyPanel/Cards/Feed/PsdNumberField';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { validateDecimalInput } from 'util/validateDecimal';
import { CancelButton } from 'containers/PropertyPanel/Cards/Feed/CancelButton';

interface PsdTableRowEditInterface {
  sizeValue: number | undefined;
  percentValue: number | undefined;
  newSizeList: number[];
  newPercentList: number[];
  index: number;
  save(saveIndex: number, editPercent: number, editSize: number): void;
  setEditIndex(index: number): void;
  hidePsd: boolean;
  cellTabbing: {
    selectedCell: boolean[];
    setSelectedCell: React.Dispatch<React.SetStateAction<boolean[]>>;
  };
  close: () => void;
}

export const PsdTableRowEdit = ({
  cellTabbing,
  sizeValue,
  percentValue,
  newSizeList,
  newPercentList,
  index,
  setEditIndex,
  save,
  hidePsd,
  close,
}: PsdTableRowEditInterface) => {
  const metricType = useMetricType();

  const [editPercent, setEditPercent] = useState<number | undefined>(
    percentValue,
  );
  const [editSize, setEditSize] = useState<number | undefined>(sizeValue);
  const ref = useRef<(HTMLDivElement | null)[]>([]);
  const validatedEditSize = validateEditSize(
    newSizeList,
    index,
    editSize,
    metricType,
  );

  const validatedEditPercent = validateEditPercent(
    newPercentList,
    newSizeList,
    editPercent,
    index,
  );

  const editError: boolean =
    validatedEditSize?.length > 0 || validatedEditPercent?.length > 0;

  const editSuccess =
    (editPercent !== newPercentList[index] ||
      editSize !== newSizeList[index]) &&
    editPercent !== undefined &&
    editSize !== undefined &&
    !editError;

  const nbrCells = !hidePsd ? 2 : 1;

  const handleSave = () => {
    if (typeof editPercent === 'number' && typeof editSize === 'number') {
      save(index, editPercent, editSize);
    }
  };

  const handleTabbing = (
    e: React.KeyboardEvent,
    cellIndex: number,
    modifier: number,
  ) => {
    e.preventDefault();

    const tmpArray = new Array(nbrCells).fill(false);

    if (
      (cellIndex === nbrCells - 1 && modifier === 1) ||
      (cellIndex === 0 && modifier === -1)
    ) {
      if (modifier === 1) {
        tmpArray[0] = true;
      } else if (modifier === -1) {
        tmpArray[nbrCells - 1] = true;
      }
      cellTabbing.setSelectedCell(() => {
        return tmpArray;
      });
      setEditIndex(index + modifier);
    } else {
      tmpArray[cellIndex + modifier] = true;
      cellTabbing.setSelectedCell(() => tmpArray);
    }
  };

  const addRowRef = useOutsideClickListener(close);

  const onSaveClicked = () => {
    editSuccess && handleSave();
    cellTabbing.setSelectedCell(Array(nbrCells).fill(false));
    setEditIndex(-1);
  };

  const handleKeyPress = (e: React.KeyboardEvent, cellIndex: number) => {
    if (e.key === 'Enter') {
      editSuccess && handleSave();
      cellTabbing.setSelectedCell(Array(nbrCells).fill(false));
      setEditIndex(-1);

      return;
    }
    if (e.key === 'Escape') {
      setEditIndex(-1);
    }

    if (e.key === 'Tab' && e.getModifierState('Shift')) {
      editSuccess && handleSave();
      handleTabbing(e, cellIndex, -1);
    } else if (e.key === 'Tab') {
      editSuccess && handleSave();
      handleTabbing(e, cellIndex, 1);
    }
  };

  useEffect(() => {
    ref.current[cellTabbing.selectedCell.indexOf(true)]?.focus();
  }, [cellTabbing.selectedCell, index]);

  return (
    <PsdGridRow ref={addRowRef}>
      <PsdCheckboxCell>
        <CancelButton onClose={close} />
      </PsdCheckboxCell>

      <PsdSizeCell>
        <PsdNumberField
          inputRef={(el) => {
            ref.current[0] = el;
          }}
          autoFocus={cellTabbing.selectedCell[0]}
          value={editSize}
          error={validatedEditSize}
          onKeyDown={(e: React.KeyboardEvent) => handleKeyPress(e, 0)}
          onChange={(e) => setEditSize(validateDecimalInput(e, 3))}
        />
      </PsdSizeCell>

      {!hidePsd && (
        <PsdPercentCell>
          <PsdNumberField
            inputRef={(el) => {
              ref.current[1] = el;
            }}
            autoFocus={cellTabbing.selectedCell[1]}
            value={editPercent}
            error={validatedEditPercent}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyPress(e, 1)}
            onChange={(e) => setEditPercent(validateDecimalInput(e))}
          />
        </PsdPercentCell>
      )}

      <PsdActionCell>
        <Tooltip title={'Save row'}>
          <IconButton
            size='small'
            disabled={!editSuccess}
            onClick={onSaveClicked}
          >
            <CheckCircleIcon color={editSuccess ? 'primary' : 'disabled'} />
          </IconButton>
        </Tooltip>
      </PsdActionCell>
    </PsdGridRow>
  );
};
